/**
 * The recovery banner (`112-offline-drafts.md` §7.3, §7.4).
 *
 * Shown when the local draft and the server have DIVERGED — either found on reload, or
 * reported by a `412 stale_write` when the outbox replayed a queued edit. Same situation,
 * same banner; there is deliberately only one conflict UI in this app.
 *
 * **We do not silently clobber either version.** Neither is authoritative and we cannot
 * know which the user wants. The editor below shows the SERVER version and is READ-ONLY
 * until they choose — showing an editable document that is about to be replaced is how
 * you get a second conflict on top of the first.
 *
 * **The banner is not dismissible.** `Esc` does not close it. A conflict the user "closes"
 * without resolving is a conflict that quietly resurfaces as data loss the next time the
 * autosave fires. It stays until one of the three buttons is clicked.
 */
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { extractText } from '@/lib/content';
import { diffLines } from '@/lib/diff';
import { cn } from '@/lib/utils';
import { currentDateLocale, useTranslation } from '@/lib/i18n';
import type { Conflict } from '@/offline/conflicts';

export interface RecoveryBannerProps {
  conflict: Conflict;
  onKeepMine: () => void;
  onUseServer: () => void;
}

export function RecoveryBanner({ conflict, onKeepMine, onUseServer }: RecoveryBannerProps) {
  const { t } = useTranslation();
  const [showDiff, setShowDiff] = useState(false);

  const when = new Date(conflict.draft.savedAt);
  const time = when.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const ago = formatDistanceToNow(when, { addSuffix: true, locale: currentDateLocale() });

  return (
    <>
      <div
        data-testid="recovery-banner"
        role="alert"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b-2 border-[var(--destructive)] bg-[var(--destructive)]/8 px-4 py-2"
      >
        <TriangleAlert
          className="size-4 shrink-0 text-[var(--destructive)]"
          strokeWidth={2}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{t('editor.recovery.unsavedChanges', { time, ago })}</p>
          <p className="text-[var(--muted-foreground)]">{t('editor.recovery.explanation')}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" data-testid="conflict-keep-mine" onClick={onKeepMine}>
            {t('editor.recovery.keepMine')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="conflict-use-server"
            onClick={onUseServer}
          >
            {t('editor.recovery.useServerVersion')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid="conflict-show-diff"
            onClick={() => {
              setShowDiff(true);
            }}
          >
            {t('editor.recovery.showDiff')}
          </Button>
        </div>
      </div>

      <Dialog open={showDiff} onOpenChange={setShowDiff}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('editor.recovery.diffTitle')}</DialogTitle>
            <DialogDescription>{t('editor.recovery.diffDescription')}</DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-auto rounded-md border border-[var(--border)] bg-[var(--muted)] p-2 font-[family-name:var(--font-mono)] text-xs">
            {diffLines(
              extractText(conflict.server.content),
              extractText(conflict.draft.content),
            ).map((line, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key -- a diff line has no id
                key={index}
                className={cn(
                  'flex gap-2 whitespace-pre-wrap',
                  line.op === 'add' && 'bg-[var(--success)]/15',
                  line.op === 'remove' && 'bg-[var(--destructive)]/15',
                )}
              >
                {/* Colour is never the only signal (110 §1.3) — the gutter mark carries it. */}
                <span
                  aria-hidden
                  className="w-3 shrink-0 text-[var(--muted-foreground)] select-none"
                >
                  {line.op === 'add' ? '+' : line.op === 'remove' ? '-' : ' '}
                </span>
                <span className="min-w-0 flex-1">{line.text === '' ? ' ' : line.text}</span>
              </div>
            ))}
          </div>

          <p className="text-[var(--muted-foreground)]">
            <span className="font-medium text-[var(--foreground)]">+</span>{' '}
            {t('editor.recovery.yourVersion')} ·{' '}
            <span className="font-medium text-[var(--foreground)]">-</span>{' '}
            {t('editor.recovery.serverVersion')}
          </p>

          <DialogFooter>
            <Button
              size="sm"
              onClick={() => {
                setShowDiff(false);
                onKeepMine();
              }}
            >
              {t('editor.recovery.keepMine')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowDiff(false);
                onUseServer();
              }}
            >
              {t('editor.recovery.useServerVersion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
